import graphql__b9226554545ce6357275 from "./__generated__/MarketingEmailDeliveryViewQuery.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useLazyLoadQuery} from "react-relay/hooks";
import {useOutletContext} from "react-router";
import {useIntl} from "react-intl";
import Panel from "./presentational/Panel";
import Table from "./presentational/table/Table";
import TableHeader from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import StatusBadge from "./presentational/StatusBadge";
import Link from "./presentational/Link";
import type {MarketingEmailDeliveryViewQuery as MarketingEmailDeliveryViewQueryType} from "./__generated__/MarketingEmailDeliveryViewQuery.graphql";

type Props = {};

const MarketingEmailDeliveryView: React.FC<Props> = () => {
    const intl = useIntl();
    const context: any = useOutletContext();
    const marketingEmail = context?.marketingEmail;

    const query = useLazyLoadQuery<MarketingEmailDeliveryViewQueryType>(
        graphql__b9226554545ce6357275,
        {marketingEmailId: marketingEmail?.id || ''},
        {fetchPolicy: 'store-and-network'}
    );

    const deliveries = query.marketingEmailDeliveries || [];
    const stats = query.marketingEmailDeliveryStats;

    const getStatusBadgeStates = () => ({
        green: [intl.formatMessage({id: 'delivery.status.delivered'}), intl.formatMessage({id: 'delivery.status.sent'})],
        yellow: [intl.formatMessage({id: 'delivery.status.queued'})],
        red: [intl.formatMessage({id: 'delivery.status.bounced'}), intl.formatMessage({id: 'delivery.status.failed'})],
    });

    const formatStatus = (status: string) => {
        switch (status) {
            case 'QUEUED': return intl.formatMessage({id: 'delivery.status.queued'});
            case 'SENT': return intl.formatMessage({id: 'delivery.status.sent'});
            case 'DELIVERED': return intl.formatMessage({id: 'delivery.status.delivered'});
            case 'BOUNCED': return intl.formatMessage({id: 'delivery.status.bounced'});
            case 'FAILED': return intl.formatMessage({id: 'delivery.status.failed'});
            default: return status;
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    const getContactName = (contact: any) => {
        if (!contact) return '-';
        if (contact.teamName) return contact.teamName;
        return [contact.givenName, contact.familyName].filter(Boolean).join(' ') || '-';
    };

    return (
        <Panel>
            <div className="px-6 py-6">
                {/* Statistics */}
                <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                        {intl.formatMessage({id: 'marketingEmail.delivery.statistics'})}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4">
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-semibold text-gray-900">{stats?.total || 0}</p>
                            <p className="text-sm text-gray-500">{intl.formatMessage({id: 'delivery.stat.total'})}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-semibold text-gray-900">{stats?.queued || 0}</p>
                            <p className="text-sm text-gray-500">{intl.formatMessage({id: 'delivery.stat.queued'})}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-semibold text-gray-900">{stats?.sent || 0}</p>
                            <p className="text-sm text-gray-500">{intl.formatMessage({id: 'delivery.stat.sent'})}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-semibold text-green-600">{stats?.delivered || 0}</p>
                            <p className="text-sm text-gray-500">{intl.formatMessage({id: 'delivery.stat.delivered'})}</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-semibold text-red-600">{stats?.bounced || 0}</p>
                            <p className="text-sm text-gray-500">{intl.formatMessage({id: 'delivery.stat.bounced'})}</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 text-center">
                            <p className="text-2xl font-semibold text-red-600">{stats?.failed || 0}</p>
                            <p className="text-sm text-gray-500">{intl.formatMessage({id: 'delivery.stat.failed'})}</p>
                        </div>
                    </div>
                </div>

                {/* Delivery List */}
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                    {intl.formatMessage({id: 'marketingEmail.delivery.list'})}
                </h3>

                {deliveries.length === 0 ? (
                    <div className="text-center py-6 text-gray-500">
                        {intl.formatMessage({id: 'marketingEmail.delivery.noDeliveries'})}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader>{intl.formatMessage({id: 'delivery.column.status'})}</TableHeader>
                                    <TableHeader>{intl.formatMessage({id: 'delivery.column.recipient'})}</TableHeader>
                                    <TableHeader>{intl.formatMessage({id: 'delivery.column.contact'})}</TableHeader>
                                    <TableHeader>{intl.formatMessage({id: 'delivery.column.sentAt'})}</TableHeader>
                                    <TableHeader>{intl.formatMessage({id: 'delivery.column.deliveredAt'})}</TableHeader>
                                    <TableHeader>{intl.formatMessage({id: 'delivery.column.details'})}</TableHeader>
                                </tr>
                            </thead>
                            <tbody>
                                {deliveries.map((delivery, index) => (
                                    <tr
                                        key={delivery.id}
                                        className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50')}
                                    >
                                        <TableData>
                                            <StatusBadge
                                                value={formatStatus(delivery.status)}
                                                states={getStatusBadgeStates()}
                                            />
                                        </TableData>
                                        <TableData>{delivery.recipientEmail}</TableData>
                                        <TableData>
                                            {delivery.contact ? (
                                                <Link to={`/contacts/${delivery.contact.id}`}>
                                                    {getContactName(delivery.contact)}
                                                </Link>
                                            ) : (
                                                '-'
                                            )}
                                        </TableData>
                                        <TableData>{formatDate(delivery.sentAt)}</TableData>
                                        <TableData>{formatDate(delivery.deliveredAt)}</TableData>
                                        <TableData>
                                            {delivery.bounceType && (
                                                <span className="text-red-600">{delivery.bounceType}</span>
                                            )}
                                        </TableData>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </div>
        </Panel>
    );
};

export default MarketingEmailDeliveryView;
